'use strict';

define('vb/private/stateManagement/fragmentHolderBaseMixin',[
  'vb/private/constants',
  'vb/private/utils',
  'vb/components/oj-vb-fragment/loader', // load fragment CCA so it's automatically available
], (Constants, Utils) => {
  /**
   * @typedef {import('vb/private/stateManagement/fragmentBridge').default} FragmentBridge
   */

  /**
   * Base mixin that has common functions that all fragment holder containers share. Whether the container is an
   * extension or the base.
   * @mixin
   */
  const FragmentHolderBaseMixin = (superclass) => class extends superclass {
    constructor(...args) {
      super(...args);

      /**
       * instances of loaded fragments used by this container.
       * @type {Object}
       */
      this.fragments = {};

      /**
       * fragmentBridge that connects this container to the component. Usually set by the fragmentBridge itself
       * @type {FragmentBridge?}
       */
      this.fragmentBridge = null;

      /**
       * Parent oj-module node cached while this fragment holder's module is connected.
       * @type {Node|undefined}
       */
      this.parentOJModuleNode = undefined;

      this.loadHtmlPromise = null; // Initialized in loadTemplate

      const parent = this.getParentOrBaseOfExtension();

      const parentAllowsSlots = (parent && typeof parent.getCapability === 'function')
        ? parent.getCapability(Constants.FragmentCapability.ALLOWS_SLOTS) : true;
      /**
       * by default fragment allows slots to be used as long as its not turned off by its parent (e.g., in layouts)
       * @type {{allowSlots: boolean}}
       */
      this.capabilities = {
        allowSlots: parentAllowsSlots === undefined ? true : parentAllowsSlots,
      };
    }

    /**
     * Returns the fragment bridge for this fragment holder.
     * @returns {FragmentBridge}
     */
    getFragmentBridge() {
      return this.fragmentBridge;
    }

    getParentOrBaseOfExtension() {
      return this.parent;
    }

    /**
     * Returns a value that is appropriate to the option that is passed in.
     * @param {String} option
     * @returns {*}
     */
    getCapability(option) {
      if (option === Constants.FragmentCapability.ALLOWS_SLOTS) {
        return this.capabilities.allowSlots;
      }
      return null;
    }

    /**
     * Return a promise to load the .html
     *
     * @returns {Promise} a promise that resolve with the html text
     */
    loadTemplate() {
      // Keep a reference of the loading promise so that multiple function can wait
      // on the same promise to be resolved.
      this.loadHtmlPromise = this.loadHtmlPromise || this.templateLoader(this.getResourcePath());

      return this.loadHtmlPromise;
    }

    /**
     * Retrieves a fragment instance for the fragment id. If a Fragment instance cannot be located this method
     * creates the Fragment instance first and then returns the fragment context. This is only called from the
     * FragmentBridge.
     * @param {string} id of the fragment
     * @param {string} name of the fragment (artifacts)
     * @param {object} params input from caller
     * @param {object} templates defined on fragment
     * @param {object} [fragmentOptions]
     */
    createFragment(id, name, params, templates, fragmentOptions) {
      let fragment = this.fragments[id];
      // If an old instance is already scheduled for disposal, do not reuse it for a new logical fragment with same id.
      if (fragment && fragment.eagerDisposeState) {
        fragment = undefined;
      }
      // if name and/or params are provided then we definitely construct a fragment, otherwise return the fragment
      // that was previously created or undefined.
      if (!fragment && name) {
        return this._ensureFragmentProvider(name).then(() => this.getFragmentClass()).then((FragmentClazz) => {
          fragment = new (FragmentClazz)(id, this, undefined, name, params, templates, fragmentOptions);
          this.fragments[id] = fragment;
          return fragment;
        });
      }

      return Promise.resolve(fragment);
    }

    /**
     * An explicitly named provider can be absent from the current partial digest. Give the registry one chance to load
     * its full manifest, then initialize the provider before the fragment resolves its resource location. Missing
     * dependency metadata is reported, but should not block full-manifest recovery because recovery exists to survive
     * incomplete partial-digest dependency declarations.
     * @param {String} name fragment name, optionally prefixed with an extension id
     * @return {Promise}
     * @private
     */
    _ensureFragmentProvider(name) {
      const { prefix } = Utils.parseQualifiedIdentifier(name, { prefixToken: ':', suffixToken: '' });
      if (!prefix || prefix === Constants.ExtensionFolders.SELF
        || prefix === Constants.ExtensionNamespaces.BASE) {
        return Promise.resolve();
      }

      const registry = this.application && this.application.extensionRegistry;
      if (!registry) {
        return Promise.resolve();
      }

      const providerVisible = registry.isDependent(this.extension.id, prefix);
      const initializeProvider = () => {
        const provider = registry.getExtensionById(prefix);
        return provider ? provider.init() : undefined;
      };
      const provider = registry.getExtensionById(prefix);
      if (!providerVisible) {
        this.log.error('Extension', this.extension.id, 'references fragment provider extension', prefix,
          'without declaring it as a dependency.');
      }
      if (provider || typeof registry.loadFullManifest !== 'function') {
        return Promise.resolve().then(initializeProvider);
      }

      this.log.warn('Loading the full extension manifest to recover fragment provider extension', prefix,
        'referenced by extension', this.extension.id);
      return registry.loadFullManifest(prefix).then(initializeProvider);
    }

    getFragment(id) {
      return this.fragments[id];
    }

    /**
     * Returns the fragment instances owned by this fragment holder.
     * @returns {Array}
     */
    getChildFragments() {
      return Object.values(this.fragments);
    }

    /**
     * Returns the class used to instantiate the fragment.
     * @returns {Promise}
     */
    getFragmentClass() {
      return Promise.resolve(Object.getPrototypeOf(this).constructor.FragmentClass);
    }

    /**
     * Author may choose to dispose fragment state under these situations -
     * i) when a fragment component is still connected to viewModel but caller indicates that they want fragment state
     * disposed. For the actual state to be disposed we require the component be 'disconnected' first so this method
     * registers the request to dispose state. The fragment CCA has to be entirely removed from the DOM, so it needs
     * to be wrapped in an oj-bind-if or oj-bind-for-each that removes the fragment DOM and recreates a new one.
     * Turning off / on an oj-bind-if condition are synchronous and causes the entire subtree including fragment CCA
     * disconnected()** / connected()  to be called. **Note: Unfortunately today calls to disconnected() does not
     * really tell us whether the fragment has been removed entirely from the subtree or just hidden. A fragment may
     * just be hidden, like if it's inside a dialog that is closed. We require that authors wrap the oj-vb-fragment
     * inside an on-bind-* for deleteState to work properly.
     * ii) If the component was disconnected first, and then a call to dispose its state comes in later. Here the
     * state is deleted immediately (after running vbExit).
     * @param {String} fragmentId
     */
    addFragmentForDispose(fragmentId) {
      // check to see if fragment can be located on container that is itself not disposed
      if (this.lifecycleState === Constants.ContainerState.DISPOSED
        || this.lifecycleState === Constants.ContainerState.EXITED) {
        // there is nothing to do here since the container state is deleted.
        this.log.warn('Unable to delete fragment state because the container with id', this.id,
          'that references the fragment', fragmentId, 'has been disposed.');
        return;
      }

      // locate the fragment on the outer container and ensure it's not disposed or itself in some limbo state.
      // Not all containers are fragmentHolders (example flow)
      const fragment = this.getFragment(fragmentId);
      if (!fragment) {
        this.log.warn('Unable to delete state for fragment with id', fragmentId,
          'because it cannot be located on the container', this.id);
        return;
      }

      // we have a fragment, but it could be in many weird states that doesn't allow us to dispose its state
      // component is still connected to viewModel
      if (fragment.fragmentComponentState === Constants.FragmentComponentState.COMPONENT_CONNECTED
        || fragment.fragmentLifecycleState === Constants.FragmentState.MODULE_CONNECTED) {
        this.log.warn('Unable to delete state for fragment', fragmentId, 'at present because it is still active!',
          'The next time component is removed from DOM and disconnected (from viewModel), the request to delete its',
          ' state will be honored.');
      }

      // if the fragment is not marked for dispose, then mark it for disposal
      if (!fragment.eagerDisposeState) {
        fragment.eagerDisposeState = Constants.FragmentEagerDisposeState.REQUESTED;
      }

      // There is one case where the component and module could both be disconnected, because an oj-bind-if parent
      // already removed its children. Then fragment deleteState comes in. If so call disposeFragmentState() right
      // away. All conditions for disposing state at the right time is in disposeFragmentState
      fragment.disposeFragmentState().catch((error) => {
        this.log.error('Unable to delete state for fragment', fragmentId, 'on container', this.id, error);
      });
    }

    /**
     * deletes a fragment from the container.
     * @param {string} id
     * @param {*} [fragment] fragment instance expected at the id
     */
    deleteFragment(id, fragment) {
      if (!fragment || this.fragments[id] === fragment) {
        delete this.fragments[id];
      }
    }

    /**
     * Traverse a set of definitions (variables or constants) and build new parameters
     * If the input parameter is already in the map, overwrite the existing definition
     * @param  {String} defName Either 'variables' or 'constants'
     * @param  {Object} allParameters the map of all the parameters
     */
    _buildParameters(defName, allParameters) {
      const parameters = allParameters;
      const variableDefs = this.getAllDefinitionOf(defName);
      Object.keys(variableDefs).forEach((name) => {
        const def = variableDefs[name];
        if (def.input === Constants.VariablePropertyInput.FROM_CALLER
          || def.input === Constants.VariablePropertyInput.FROM_URL) {
          if (parameters[name]) {
            this.log.warn('Input parameter', name, 'is already defined. Using the definition in', defName);
          }
          const inputParameterValue = this.getInputParameterValue(name, def);
          parameters[name] = inputParameterValue;
          this.log.info('Input parameter', name, '[input=', def.input, '] value:', inputParameterValue);
        }
      });
    }

    /**
     * Build a map of all possible input parameters to be used in $parameters for the
     * vbBeforeEnter event
     * @return {Object} a map of parameters
     */
    buildAllParameters() {
      // Add variables input parameters
      const parameters = {};
      this._buildParameters('variables', parameters);
      // Add constants input parameters
      // If an input parameter is already defined as a variable, the constant is used.
      this._buildParameters('constants', parameters);
      return parameters;
    }

    /**
     * Checks whether the current container has any dirty data variables.
     * It will check its current scope, any embedded fragments, layouts
     * and their extensions if present.
     * @return {boolean} true if there is at least one dirty data variable found;
     *                   false otherwise
     */
    checkForDirtyData() {
      // verify if any contained fragments have dirty data
      const hasDirtyData = Object.values(this.fragments).find((fragment) => fragment.checkForDirtyData());
      return hasDirtyData ? true : super.checkForDirtyData();
    }

    /**
     * Re-sets the dirty data state of variables in the current container's scope
     * as well as fragments and layouts and their extensions.
     */
    resetDirtyData() {
      // reset all fragment scopes
      Object.values(this.fragments).forEach((fragment) => {
        fragment.resetDirtyData();
      });

      super.resetDirtyData();
    }

    /**
     * Sets the ParentOJModuleNode for the container.
     * @param {Node|undefined} parentNode
     */
    setParentOJModuleNode(parentNode) {
      this.parentOJModuleNode = parentNode;
    }

    dispose() {
      // Terminal disposal must not rely on later CCA disconnected callbacks for oj-module observer cleanup. Some
      // teardown paths dispose a fragment first, which removes it from parent.fragments; a later
      // FragmentBridge.disconnected() can then miss the fragment and skip componentDisconnected(). Unobserve and clear
      // the cached module node here as the final owner of this fragment holder's DOM reference. The unobserve call is
      // synchronous and idempotent, so it is safe if module disconnected/componentDisconnected already unobserved it.
      Utils.vbModuleObserver.unobserveModuleNode(this.parentOJModuleNode);
      this.setParentOJModuleNode(undefined);

      Object.values(this.fragments).forEach((fragment) => fragment.dispose());
      this.fragments = {};

      super.dispose();
    }
  };

  return FragmentHolderBaseMixin;
});

